import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

// Interfaces
import { LoginFormData, PasswordResetBody, RetailerInfo, TWOFACTOR_TYPE } from "@app/modules/auth/entities";

// Consts
import { environment as env } from "@environments/environment";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class CargoAuthService {
  constructor(private http: HttpClient) {}

  initialAuthentication(formdata: LoginFormData) {
    return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/login`, formdata);
  }

  turnOnTwoFactorMethod(method: TWOFACTOR_TYPE, otp: string, accessToken: string, fullhash?: string, id?: number) {
    switch (method) {
      case TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR:
        return this.http.post(
          `${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/google/turn-on`,
          {
            two_factor_authentication_code: otp,
            id,
          },
          {
            headers: {
              "x-auth-token": accessToken,
            },
          }
        );
      case TWOFACTOR_TYPE.PHONE:
      default:
        return this.http.post(
          `${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/phone/turn-on`,
          {
            two_factor_authentication_code: otp,
            fullhash,
          },
          {
            headers: {
              "x-auth-token": accessToken,
            },
          }
        );
    }
  }

  setupTwoFactorMethod(method: TWOFACTOR_TYPE, accessToken: string, phone: string | undefined, nickname: undefined | string) {
    if (method === TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR) {
      return this.http.post(
        `${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/google/generate-secret`,
        {
          nickname,
        },
        {
          headers: {
            "x-auth-token": accessToken,
          },
        }
      );
    } else if (method === TWOFACTOR_TYPE.PHONE) {
      return this.http.post(
        `${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/phone/generate-secret`,
        { phone },
        {
          headers: {
            "x-auth-token": accessToken,
          },
        }
      );
    }
  }

  setupAdditionalTwoFactorMethod(method: TWOFACTOR_TYPE, phone?: string, nickname?: string) {
    if (method === TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR) {
      return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/google/generate-secret`, { nickname });
    } else if (method === TWOFACTOR_TYPE.PHONE) {
      return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/phone/generate-secret`, { phone });
    }
  }

  turnOnAdditionalTwoFactorMethod(method: TWOFACTOR_TYPE, otp: string, fullhash?: string, authenticatorId?: number) {
    switch (method) {
      case TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR:
        return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/google/turn-on`, {
          two_factor_authentication_code: otp,
          id: authenticatorId,
        });
      case TWOFACTOR_TYPE.PHONE:
      default:
        return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/phone/turn-on`, {
          two_factor_authentication_code: otp,
          fullhash,
        });
    }
  }

  deleteTwoFactorMethod(method: TWOFACTOR_TYPE, otp: string, fullhash?: string) {
    switch (method) {
      case TWOFACTOR_TYPE.GOOGLE_AUTHENTICATOR:
        return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/google/turn-on`, {
          two_factor_authentication_code: otp,
        });
      case TWOFACTOR_TYPE.PHONE:
      default:
        return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/phone/turn-on`, {
          two_factor_authentication_code: otp,
          fullhash,
        });
    }
  }

  requestPhoneOTP(id: number, access_token: string): Observable<any> {
    if (id) {
      return this.http.post(
        `${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/phone/request-otp`,
        { id },
        {
          headers: {
            "x-auth-token": access_token,
          },
        }
      );
    }
  }

  twoFactorTokenLogin(
    access_token: string,
    two_factor_authentication_code: string,
    twofactortype: TWOFACTOR_TYPE,
    id: number,
    fullhash?: string
  ): Observable<any> {
    return this.http.post(
      `${env.gateway.base_url}/api/ms/cargo-core-api/portal/login/2fa`,
      {
        two_factor_authentication_code,
        type: twofactortype,
        fullhash,
        id,
      },
      {
        headers: {
          "x-auth-token": access_token,
        },
      }
    );
  }

  listTwoFactorConfigs(): Observable<any> {
    return this.http.get(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/validated-otp-configs`);
  }

  deleteConfig(configId: number): Observable<any> {
    return this.http.delete(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/2fa/remove/${configId}`);
  }

  createRetailer(values: RetailerInfo): Observable<any> {
    return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/retailer`, values);
  }

  passwordReset(values: PasswordResetBody): Observable<any> {
    return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/auth-reset-password`, values);
  }

  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/auth-reset-password-request`, { email });
  }

  fetchRetailerIds(resp: { access_token: string }): Observable<any> {
    return this.http.get(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/user/user-info`, {
      headers: {
        "x-auth-token": resp.access_token,
      },
    });
  }

  fetchPermissionsGroup(resp: { access_token: string }): Observable<any> {
    return this.http.get(`${env.gateway.base_url}/api/ms/cargo-core-api/portal/authorization/own-permission-group`, {
      headers: {
        "x-auth-token": resp.access_token,
      },
    });
  }

  fetch2FADeadline(): Observable<any> {
    return this.http.get(`${env.gateway.base_url}/api/ms/cargo-core-api/config/mandatory-2fa-date`);
  }
}
